/**
 * Autosave Hook
 *
 * Provides automatic configuration backup and recovery features:
 * - Periodic autosave to localStorage
 * - Recovery prompt on page load if autosave exists
 * - Unsaved changes warning on page unload
 */

import { useEffect, useCallback, useState } from 'react';
import { DatacenterConfig } from '../types/configurator';

const AUTOSAVE_KEY = 'hermit-autosave';
const AUTOSAVE_TIMESTAMP_KEY = 'hermit-autosave-timestamp';
const AUTOSAVE_INTERVAL_MS = 30000; // 30 seconds

interface AutosaveData {
  config: DatacenterConfig;
  timestamp: number;
}

interface UseAutosaveOptions {
  config: DatacenterConfig;
  isDirty: boolean;
  onRestore?: (config: DatacenterConfig) => void;
  enabled?: boolean;
}

interface UseAutosaveResult {
  hasAutosave: boolean;
  autosaveTime: Date | null;
  restore: () => void;
  dismiss: () => void;
  save: () => void;
  clear: () => void;
}

export function useAutosave({
  config,
  isDirty,
  onRestore,
  enabled = true,
}: UseAutosaveOptions): UseAutosaveResult {
  const [autosaveData, setAutosaveData] = useState<AutosaveData | null>(null);
  const [hasCheckedStorage, setHasCheckedStorage] = useState(false);

  // Check for existing autosave on mount
  useEffect(() => {
    if (!enabled || hasCheckedStorage) return;

    try {
      const saved = localStorage.getItem(AUTOSAVE_KEY);
      const timestamp = localStorage.getItem(AUTOSAVE_TIMESTAMP_KEY);

      if (saved && timestamp) {
        const parsedConfig = JSON.parse(saved) as DatacenterConfig;
        const parsedTimestamp = parseInt(timestamp, 10);

        // Only show recovery if autosave is less than 24 hours old
        const maxAge = 24 * 60 * 60 * 1000;
        if (Date.now() - parsedTimestamp < maxAge) {
          setAutosaveData({
            config: parsedConfig,
            timestamp: parsedTimestamp,
          });
        } else {
          // Clear old autosave
          clearAutosave();
        }
      }
    } catch {
      // Ignore parse errors
      clearAutosave();
    }

    setHasCheckedStorage(true);
  }, [enabled, hasCheckedStorage]);

  // Save function
  const save = useCallback(() => {
    if (!enabled) return;

    try {
      localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(config));
      localStorage.setItem(AUTOSAVE_TIMESTAMP_KEY, Date.now().toString());
    } catch (error) {
      console.warn('Autosave failed:', error);
    }
  }, [config, enabled]);

  // Clear autosave
  const clearAutosave = useCallback(() => {
    try {
      localStorage.removeItem(AUTOSAVE_KEY);
      localStorage.removeItem(AUTOSAVE_TIMESTAMP_KEY);
    } catch {
      // Ignore errors
    }
  }, []);

  const clear = useCallback(() => {
    clearAutosave();
    setAutosaveData(null);
  }, [clearAutosave]);

  // Periodic autosave when dirty
  useEffect(() => {
    if (!enabled || !isDirty) return;

    const interval = setInterval(save, AUTOSAVE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [enabled, isDirty, save]);

  // Save on beforeunload if dirty
  useEffect(() => {
    if (!enabled) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        save();
        // Show browser's native "unsaved changes" warning
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [enabled, isDirty, save]);

  // Restore function
  const restore = useCallback(() => {
    if (autosaveData && onRestore) {
      onRestore(autosaveData.config);
      clear();
    }
  }, [autosaveData, onRestore, clear]);

  // Dismiss function
  const dismiss = useCallback(() => {
    clear();
  }, [clear]);

  return {
    hasAutosave: autosaveData !== null,
    autosaveTime: autosaveData ? new Date(autosaveData.timestamp) : null,
    restore,
    dismiss,
    save,
    clear,
  };
}

/**
 * Format relative time for display
 */
export function formatRelativeTime(date: Date): string {
  const now = Date.now();
  const diff = now - date.getTime();

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  return date.toLocaleDateString();
}
